/**
 * Wrapper gated por env para Sentry SDK (`@sentry/react`) en el frontend.
 *
 * Implementación con `import()` dinámico de string literal:
 *   1. El paquete `@sentry/react` está instalado (F9-follow). Sin instalación
 *      el dynamic import fallaría en runtime y el wrapper caería al catch.
 *   2. El import dinámico con string literal permite que Vite cree un chunk
 *      code-split aparte (`sentry-*.js`) que SOLO se carga cuando
 *      `VITE_SENTRY_DSN` está seteado. Sin DSN, el chunk nunca se baja y el
 *      bundle principal NO crece.
 *   3. Importante: previamente usábamos `const path = '@sentry/react';
 *      await import(/* @vite-ignore *\/ path)` para evitar que tsc resolviera
 *      el module en compile-time, pero ese patrón rompe en el browser — el
 *      ESM nativo no puede resolver bare module specifiers en runtime sin un
 *      bundler. Con string literal Vite emite el chunk con su URL absoluta.
 *
 * Frontend var convention (Vite): variables que arrancan con `VITE_` quedan
 * expuestas al bundle del cliente. Para Sentry: `VITE_SENTRY_DSN`.
 */

interface SentryReactMinimal {
  init: (opts: Record<string, unknown>) => void;
  captureException: (err: unknown, ctx?: Record<string, unknown>) => void;
  addBreadcrumb: (crumb: Record<string, unknown>) => void;
}

let sentryRef: SentryReactMinimal | null = null;

/**
 * Lista mínima de keys sensibles a redactar antes de enviar a Sentry.
 * El frontend NO tiene `pii-redact.ts` (es server-side); replicamos las
 * redacciones críticas inline. Si esta lista crece, considerar portar
 * el helper al frontend o moverlo a `packages/connector-sdk`.
 */
const SENSITIVE_KEYS = new Set([
  'tokenHash',
  'accessToken',
  'refreshToken',
  'apiKey',
  'password',
  'authorization',
  'cookie',
]);

function redactValue(key: string, value: unknown): unknown {
  if (SENSITIVE_KEYS.has(key)) return '***redacted***';
  // Email: preserva primer char + dominio
  if ((key === 'email' || key.endsWith('Email')) && typeof value === 'string') {
    const atIdx = value.indexOf('@');
    if (atIdx >= 0) {
      const local = value.slice(0, atIdx);
      const domain = value.slice(atIdx + 1);
      return local.length <= 1 ? `***@${domain}` : `${local[0]}***@${domain}`;
    }
  }
  return value;
}

function deepRedact(node: unknown, depth = 0): unknown {
  if (depth > 6) return node;
  if (node === null || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map((item) => deepRedact(item, depth + 1));
  const obj = node as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(key)) {
      out[key] = '***redacted***';
    } else if (key === 'email' || key.endsWith('Email')) {
      out[key] = redactValue(key, value);
    } else {
      out[key] = deepRedact(value, depth + 1);
    }
  }
  return out;
}

function beforeSendHook(event: unknown): unknown {
  try {
    return deepRedact(event);
  } catch {
    return event;
  }
}

/**
 * Inicializa Sentry React si:
 *   1. `VITE_SENTRY_DSN` está seteado, Y
 *   2. `@sentry/react` está instalado.
 *
 * Si falla, log a console y sigue. NO lanza nunca.
 */
export async function initSentryIfAvailable(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || String(dsn).trim().length === 0) {
    return;
  }

  try {
    // String literal: Vite crea un chunk code-split aparte que solo se carga
    // si llegamos a este branch (VITE_SENTRY_DSN seteado). Bundle principal
    // no crece para tenants que no usan Sentry.
    const sentry = (await import('@sentry/react')) as unknown as SentryReactMinimal & {
      browserTracingIntegration?: () => unknown;
      replayIntegration?: () => unknown;
    };

    const integrations: unknown[] = [];
    if (typeof sentry.browserTracingIntegration === 'function') {
      integrations.push(sentry.browserTracingIntegration());
    }
    // Replay: opcional, decide el operador con env aparte
    if (
      import.meta.env.VITE_SENTRY_REPLAY === '1' &&
      typeof sentry.replayIntegration === 'function'
    ) {
      integrations.push(sentry.replayIntegration());
    }

    const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1');

    sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
      integrations,
      beforeSend: beforeSendHook,
      sendDefaultPii: false,
    });

    sentryRef = sentry;
     
    console.log('[sentry] inicializado');
  } catch (err) {
     
    console.warn('[sentry] no se pudo inicializar:', err);
  }
}

/**
 * Captura una excepción si Sentry está activo. NO-op si no.
 * Se usa desde el ErrorBoundary y desde código que quiera reportar manual.
 */
export function captureExceptionIfAvailable(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!sentryRef) return;
  try {
    sentryRef.captureException(
      err,
      context ? { extra: deepRedact(context) as Record<string, unknown> } : undefined,
    );
  } catch {
    // never throw from observability path
  }
}

export function addBreadcrumbIfAvailable(crumb: Record<string, unknown>): void {
  if (!sentryRef) return;
  try {
    sentryRef.addBreadcrumb(deepRedact(crumb) as Record<string, unknown>);
  } catch {
    // never throw
  }
}
